import * as React from "react"
import { Combobox as ComboboxPrimitive } from "@base-ui/react/combobox"
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { matchesAllWords } from "@/lib/search"

export interface ComboboxOption {
  id: number
  name: string
}

// A typeable single-select (typeahead) over a fixed list of {id, name} options.
// Filters as you type; commits the selected option's id via onValueChange.
function Combobox({
  options,
  value,
  onValueChange,
  placeholder = "Select…",
  emptyText = "No matches.",
  disabled,
  className,
  onInputKeyDown,
}: {
  options: ComboboxOption[]
  value: number | null
  onValueChange: (id: number | null) => void
  placeholder?: string
  emptyText?: string
  disabled?: boolean
  className?: string
  onInputKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
}) {
  const selected = React.useMemo(
    () => options.find((o) => o.id === value) ?? null,
    [options, value]
  )

  return (
    <ComboboxPrimitive.Root
      items={options}
      value={selected}
      onValueChange={(v) => onValueChange(v?.id ?? null)}
      itemToStringLabel={(o) => o?.name ?? ""}
      isItemEqualToValue={(a, b) => a?.id === b?.id}
      // Multi-word "all words must match": typing "Rama priya" finds "Rama … priya" (order-independent,
      // substring), instead of the default whole-string contains match.
      filter={(item, query) => matchesAllWords(query, (item as ComboboxOption)?.name)}
      disabled={disabled}
    >
      <div className={cn("relative", className)}>
        <ComboboxPrimitive.Input
          placeholder={placeholder}
          disabled={disabled}
          onKeyDownCapture={onInputKeyDown}
          data-slot="combobox-input"
          className={cn(
            "flex h-9 w-full items-center rounded-lg border border-input bg-transparent py-2 pr-9 pl-2.5 text-sm transition-colors outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30"
          )}
        />
        <ComboboxPrimitive.Trigger
          disabled={disabled}
          aria-label="Open"
          className="absolute inset-y-0 right-0 flex items-center px-2.5 text-muted-foreground outline-none disabled:opacity-50"
        >
          <ComboboxPrimitive.Icon
            render={<ChevronsUpDownIcon className="size-4 shrink-0" />}
          />
        </ComboboxPrimitive.Trigger>
      </div>

      <ComboboxPrimitive.Portal>
        <ComboboxPrimitive.Positioner sideOffset={4} className="isolate z-50">
          <ComboboxPrimitive.Popup
            data-slot="combobox-content"
            className="max-h-(--available-height) w-(--anchor-width) min-w-36 origin-(--transform-origin) overflow-y-auto rounded-lg bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95"
          >
            <ComboboxPrimitive.Empty className="text-muted-foreground px-2 py-4 text-center text-sm">
              {emptyText}
            </ComboboxPrimitive.Empty>
            <ComboboxPrimitive.List>
              {(option: ComboboxOption) => (
                <ComboboxPrimitive.Item
                  key={option.id}
                  value={option}
                  className="relative flex w-full cursor-pointer items-center gap-1.5 rounded-md py-1.5 pr-8 pl-2 text-sm outline-none select-none data-highlighted:bg-accent data-highlighted:text-accent-foreground"
                >
                  <span className="flex-1 truncate">{option.name}</span>
                  <ComboboxPrimitive.ItemIndicator className="absolute right-2 flex items-center">
                    <CheckIcon className="size-4" />
                  </ComboboxPrimitive.ItemIndicator>
                </ComboboxPrimitive.Item>
              )}
            </ComboboxPrimitive.List>
          </ComboboxPrimitive.Popup>
        </ComboboxPrimitive.Positioner>
      </ComboboxPrimitive.Portal>
    </ComboboxPrimitive.Root>
  )
}

export { Combobox }
